import { useMemo } from "react";

/** Extract index name from symbol */
function getIndexFromSymbol(symbol) {
  if (!symbol) return null;
  const s = symbol.replace("NSE:", "").toUpperCase();

  if (s.includes("MCX")) return null;
  if (s.includes("BANKNIFTY")) return "BANKNIFTY";
  if (s.includes("FINNIFTY") || s.includes("FIN NIFTY")) return "FINNIFTY";
  if (s.includes("MIDCPNIFTY") || s.includes("MIDCP")) return "MIDCPNIFTY";
  if (s.includes("SENSEX") || s.includes("BSX")) return "SENSEX";
  if (s.includes("NIFTY")) return "NIFTY";

  return null;
}

/** Safe number parser */
function safeNum(v, d = 0) {
  const n = Number(v);
  return isNaN(n) ? d : n;
}

/** Normalize BUY/SELL */
function normalizeSide(side) {
  if (!side) return "";
  const s = Number(side);
  if (s === 1) return "BUY";
  if (s === -1) return "SELL";
  return String(side).toUpperCase();
}

/** Check if date is today */
function isToday(date) {
  if (!date) return false;
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}

export function useRejectedOrdersView(
  rejectedOrders,
  safeUserMappings,
  ordersUpdateVersion
) {
  const groupedRejected = useMemo(() => {
    if (!rejectedOrders || rejectedOrders.length === 0) return [];

    // Step 1 → Filter status_no = 5 (Rejected) AND only Today's orders
    const filtered = rejectedOrders.filter((o) => {
      if (String(o.status_no) !== "5") return false;
      const t = o.orderdatetime ? new Date(o.orderdatetime) : null;
      return isToday(t);
    });

    // Step 2 → Deduplicate by client_id + qty + strategy (pick latest by orderdatetime)
    const latestMap = new Map();
    filtered.forEach((o) => {
      const key = `${o.client_id}-${o.qty}-${o.strategy}`;
      const existing = latestMap.get(key);

      if (!existing) {
        latestMap.set(key, o);
      } else {
        const existingTime = new Date(existing.orderdatetime).getTime();
        const newTime = new Date(o.orderdatetime).getTime();
        if (newTime > existingTime) {
          latestMap.set(key, o);
        }
      }
    });
    const dedupedOrders = Array.from(latestMap.values());

    // Step 3 → Normalize each order
    const processed = dedupedOrders.map((o) => {
      const symbol = o.symbol || "";
      return {
        raw: o,
        orderId: o.order_id,
        clientId: o.client_id || "",
        symbol,
        cleanSymbol: symbol.replace("NSE:", "").toUpperCase(),
        index: getIndexFromSymbol(symbol),
        strategy: o.strategy || "-",
        qty: safeNum(o.qty),
        side: normalizeSide(o.side),
        status_no: o.status_no,
      };
    });

    // Step 4 → Group by key (symbol, qty, side, strategy)
    const map = new Map();

    processed.forEach((o) => {
      const key = `${o.symbol}-${o.qty}-${o.side}-${o.strategy}`;
      if (!map.has(key)) {
        map.set(key, {
          symbol: o.symbol,
          cleanSymbol: o.cleanSymbol,
          qty: o.qty,
          side: o.side,
          strategy: o.strategy,
          index: o.index,
          clientsSet: new Set(),
          orders: [],
        });
      }
      const entry = map.get(key);
      entry.clientsSet.add(o.clientId);
      entry.orders.push(o);
    });

    // Step 5 → Convert Set → Array & add client names
    return Array.from(map.values()).map((row) => {
      const clients = Array.from(row.clientsSet || []).map((clientId) => {
        const user = safeUserMappings.find((u) => u.client_id === clientId);
        return {
          clientId,
          name: user?.name || "Unknown",
          status: user?.status || "Inactive",
        };
      });

      return {
        ...row,
        clients,
        count: row.orders.length,
      };
    });
  }, [rejectedOrders, safeUserMappings, ordersUpdateVersion]);

  return groupedRejected;
}
